import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Route-level code splitting (plan 45 §2): each page is its own chunk, loaded
 * when the route opens. The same loader preloads the chunk when a navigation
 * link is hovered or focused, and for the busiest pages once the first paint
 * is done, so most navigations find the chunk already in the browser.
 */
export type Loader = () => Promise<unknown>;

export type PreloadableComponent<P> = LazyExoticComponent<ComponentType<P>> & {
  preload: Loader;
};

/** Memoises a loader: the import runs once, later calls share the promise. */
export function once(loader: Loader): Loader {
  let pending: Promise<unknown> | undefined;
  return () => {
    pending ??= loader().catch((error: unknown) => {
      // A failed chunk (offline, a new deploy) may load on the next try.
      pending = undefined;
      throw error;
    });
    return pending;
  };
}

/**
 * `React.lazy` for a named export, with `preload()`. Pages keep their named
 * exports; `pick` chooses the component from the module.
 */
export function lazyPage<M, P>(
  loader: () => Promise<M>,
  pick: (module: M) => ComponentType<P>,
): PreloadableComponent<P> {
  const load = once(loader) as () => Promise<M>;
  const component = lazy(async () => ({ default: pick(await load()) })) as PreloadableComponent<P>;
  component.preload = load;
  return component;
}

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, options?: { timeout: number }) => number;
};

/** Runs `task` when the browser is idle (or after `timeout` ms where idle callbacks are missing). */
export function whenIdle(task: () => void, timeout = 2000): void {
  const w = (typeof window === 'undefined' ? undefined : window) as IdleWindow | undefined;
  if (!w) return;
  if (typeof w.requestIdleCallback === 'function') w.requestIdleCallback(task, { timeout });
  else w.setTimeout(task, Math.min(timeout, 1000));
}

/**
 * Preloads route chunks after the first paint, one idle slot each, so the
 * download never competes with the page the person opened. Failures are
 * ignored: the route loads normally when opened.
 */
export function preloadAfterPaint(loaders: readonly Loader[], delayMs = 2000): void {
  const queue = [...loaders];
  const next = () => {
    const loader = queue.shift();
    if (!loader) return;
    loader().then(
      () => whenIdle(next),
      () => whenIdle(next),
    );
  };
  // Two frames (the first paint is done), then `delayMs` so the page's own data and
  // rendering finish first, then one idle slot per chunk.
  const start = () => (delayMs > 0 ? setTimeout(() => whenIdle(next), delayMs) : whenIdle(next));
  if (typeof requestAnimationFrame === 'function')
    requestAnimationFrame(() => requestAnimationFrame(start));
  else start();
}

/**
 * Props for a navigation link that preload its route on hover and focus
 * (pointer and keyboard users alike). `preloadFor` returns the loader for an
 * href, or undefined when the route is already in the main chunk.
 */
export function preloadProps(href: string, preloadFor: (href: string) => Loader | undefined) {
  const run = () => {
    void preloadFor(href)?.().catch(() => undefined);
  };
  return { onMouseEnter: run, onFocus: run, onTouchStart: run };
}

/**
 * Starts loading the font weights the first screen uses while config and
 * sign-in load, so text does not swap (and re-wrap) after the first render.
 * The HTML preloads one file (the body weight); these start from script,
 * Latin only, and never block rendering.
 */
export function warmFonts(
  weights: readonly number[] = [500, 600, 700],
  family = 'Plus Jakarta Sans',
  fonts: Pick<FontFaceSet, 'load'> | undefined = typeof document === 'undefined'
    ? undefined
    : document.fonts,
): void {
  if (!fonts || typeof fonts.load !== 'function') return;
  for (const weight of weights) void fonts.load(`${weight} 1em "${family}"`).catch(() => undefined);
}
